import { useState } from "react";
import { Link } from "react-router-dom";
import { AtSymbolIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import useTranslation from "@babackapp/translations/use-translation";
import { useAuth } from "@babackapp/auth-client/hooks";
import logoColors from "@/assets/logo-colors.png";

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Adresse e-mail invalide." }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const t = useTranslation();
  const auth = useAuth();

  const emailFromUrl = new URLSearchParams(window.location.search).get("email");
  const token = new URLSearchParams(window.location.search).get("token");
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: emailFromUrl || "",
    },
  });

  const resetPassword = async (data: ForgotPasswordFormValues) => {
    try {
      await auth.forgotPassword(data.email);
      setEmailSent(true);
    } catch (error: any) {
      console.error(error);
      window.alert(
        "Une erreur s'est produite lors de la réinitialisation du mot de passe.",
      );
    }
  };

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50 h-screen">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img className="mx-auto h-12 w-auto" src={logoColors} alt="Baback" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t("forgotPwd.title")}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t("misc.or")}{" "}
          <Link
            to={`/auth/sign-in${token || emailFromUrl ? "?" : ""}${
              token ? "token=" + token : ""
            }${emailFromUrl ? "&email=" + emailFromUrl : ""}`}
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            {t("login.login")}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {emailSent ? (
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500 mr-2" />
              <div>
                {t("forgotPwd.success")} {emailFromUrl || ""}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(resetPassword)}>
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("login.email")}
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <AtSymbolIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <Input
                      id="email"
                      type="email"
                      {...register("email")}
                      className="pl-10"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={auth.isLoading}
                  className="w-full"
                >
                  {t("forgotPwd.reset")}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
